import { useEffect, useRef, useState } from 'react'
import { Gift } from 'lucide-react'
import {
  CLICK_GUARD_MS, FAB_MARGIN, FAB_SIZE, clamp, edgePoint, isDragMove,
  parsePlace, snapToEdge, tapSlop,
} from './fabGeometry'

/**
 * Плавающая кнопка колеса фортуны.
 *
 * Висит у края экрана, её можно перетащить в любую точку по краям — после
 * отпускания она притягивается к ближайшему краю и запоминает позицию.
 * Позиция хранится долей вдоль края (0..1), поэтому не ломается при смене
 * размера экрана или повороте телефона.
 */

// Приложение на широком экране центрировано и ограничено 480 px (MainLayout).
// Кнопка должна липнуть к краю приложения, а не к краю монитора.
const APP_MAX_WIDTH = 480

function currentUserId() {
  try {
    return window.Telegram?.WebApp?.initDataUnsafe?.user?.id || 0
  } catch {
    return 0
  }
}

function storageKey() {
  return `scalpex-fortune-fab-${currentUserId() || 'anon'}`
}

function readSaved(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSaved(key, place) {
  try {
    localStorage.setItem(key, JSON.stringify(place))
  } catch { /* приватный режим — позиция просто не запомнится */ }
}

export default function FortuneFab({ onClick, label }) {
  const keyRef = useRef(storageKey())
  const dragRef = useRef(null)
  const posRef = useRef(null)
  const movedRef = useRef(false)
  // «Хвостовой» click после pointerup: окно уже открыто или это конец
  // перетаскивания — повторно реагировать на него нельзя.
  const suppressClickRef = useRef(false)
  const suppressTimerRef = useRef(null)

  const [dims, setDims] = useState(() => ({
    w: typeof window === 'undefined' ? 390 : window.innerWidth,
    h: typeof window === 'undefined' ? 800 : window.innerHeight,
  }))
  const [place, setPlace] = useState(() => parsePlace(readSaved(keyRef.current)))
  const [drag, setDrag] = useState(null)

  useEffect(() => {
    const onResize = () => setDims({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  // Гасим клик, который браузер шлёт сразу после pointerup. Иначе он либо
  // открывает окно второй раз, либо (если кнопка уже сменилась окном)
  // попадает в подложку и мгновенно закрывает его — снаружи это выглядит
  // как «кнопка не сработала».
  const suppressClick = () => {
    suppressClickRef.current = true
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current)
    suppressTimerRef.current = setTimeout(() => {
      suppressClickRef.current = false
      suppressTimerRef.current = null
    }, CLICK_GUARD_MS)
  }

  useEffect(() => () => {
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current)
  }, [])

  // Область приложения внутри окна: на телефоне совпадает с экраном, на
  // широком мониторе — центральная колонка 480 px.
  const shellW = Math.min(dims.w, APP_MAX_WIDTH)
  const offsetX = (dims.w - shellW) / 2
  const baseRel = edgePoint(place.edge, place.ratio, shellW, dims.h)
  const base = { x: baseRel.x + offsetX, y: baseRel.y }
  const pos = drag || base

  useEffect(() => { posRef.current = pos }, [pos])

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* старый WebView */ }
    // Стартуем от того места, где кнопка реально нарисована: сразу после
    // прилипания к краю ещё идёт анимация, а в state уже лежит цель.
    const rect = e.currentTarget.getBoundingClientRect()
    dragRef.current = {
      id: e.pointerId,
      slop: tapSlop(e.pointerType),
      startX: e.clientX,
      startY: e.clientY,
      x: rect.left,
      y: rect.top,
    }
    movedRef.current = false
  }

  const onPointerMove = (e) => {
    const d = dragRef.current
    if (!d || d.id !== e.pointerId) return
    const dx = e.clientX - d.startX
    const dy = e.clientY - d.startY
    // Пока палец не ушёл дальше допуска тапа — это ещё нажатие, кнопка
    // не дёргается. Для мыши допуск строже, чем для пальца.
    if (!movedRef.current && !isDragMove(dx, dy, d.slop)) return
    movedRef.current = true

    const minX = offsetX + FAB_MARGIN
    const maxX = Math.max(minX, offsetX + shellW - FAB_MARGIN - FAB_SIZE)
    const maxY = Math.max(FAB_MARGIN, dims.h - FAB_MARGIN - FAB_SIZE)
    const next = {
      x: clamp(d.x + dx, minX, maxX),
      y: clamp(d.y + dy, FAB_MARGIN, maxY),
    }
    posRef.current = next
    setDrag(next)
  }

  const onPointerUp = (e) => {
    const d = dragRef.current
    if (!d || d.id !== e.pointerId) return
    dragRef.current = null

    if (!movedRef.current) {
      // Тап: открываем окно и гасим «хвостовой» click браузера.
      suppressClick()
      setDrag(null)
      if (onClick) onClick()
      return
    }

    const p = posRef.current || base
    const snapped = snapToEdge(p.x - offsetX, p.y, shellW, dims.h)
    const nextPlace = { edge: snapped.edge, ratio: snapped.ratio }
    suppressClick()
    setPlace(nextPlace)
    setDrag(null)
    writeSaved(keyRef.current, nextPlace)
  }

  const onPointerCancel = () => {
    dragRef.current = null
    movedRef.current = false
    setDrag(null)
  }

  // Страховка: если браузер отобрал захват указателя посреди жеста,
  // не оставляем кнопку «залипшей» в состоянии перетаскивания.
  const onLostPointerCapture = (e) => {
    const d = dragRef.current
    if (!d || d.id !== e.pointerId) return
    dragRef.current = null
    if (movedRef.current) suppressClick()
    movedRef.current = false
    setDrag(null)
  }

  // Тап и перетаскивание обрабатывают pointer-события. Click нужен только
  // как запасной путь: клавиатура (detail 0) и старые WebView без pointerup.
  // Обычный click сразу после pointerup гасим — иначе окно открылось бы
  // дважды или мгновенно закрылось от того же нажатия.
  const handleClick = (e) => {
    if (e.detail === 0) {
      if (onClick) onClick()
      return
    }
    if (suppressClickRef.current) return
    if (onClick) onClick()
  }

  return (
    <button
      type="button"
      className={`fortune-fab${drag ? ' is-dragging' : ''}`}
      style={{ left: `${pos.x}px`, top: `${pos.y}px` }}
      aria-label={label}
      title={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
      onClick={handleClick}
      onContextMenu={e => e.preventDefault()}
    >
      <Gift size={24} strokeWidth={2.2} />
    </button>
  )
}
